/**
 * Panel del asistente de la academia: **deflexión** + la FAQ que la produce.
 *
 * La cifra que importa es la tasa de deflexión: de cada 100 preguntas, cuántas
 * resolvió el bot sin molestar a nadie. Y justo al lado, las preguntas que NO
 * supo responder — que es de donde salen las entradas de FAQ que suben esa
 * cifra. Sin ese bucle, el chatbot es una caja negra que a veces acierta.
 */
import { type Component, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Check, MessageCircleQuestion, Plus, Trash2 } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import {
  useDeflection,
  useDeleteKbEntry,
  useKbEntries,
  useResolveUnanswered,
  useUnanswered,
  useUpsertKbEntry,
} from "./api";

const PERCENT = 100;

export const AcademyBotPanel: Component = () => {
  const { t } = useI18n();
  const { activeOrgId } = useActiveOrg();
  const deflection = useDeflection(activeOrgId);
  const entries = useKbEntries(activeOrgId);
  const unanswered = useUnanswered(activeOrgId);
  const upsert = useUpsertKbEntry(activeOrgId);
  const remove = useDeleteKbEntry(activeOrgId);
  const resolve = useResolveUnanswered(activeOrgId);
  const [question, setQuestion] = createSignal("");
  const [answer, setAnswer] = createSignal("");

  const rate = () => Math.round((deflection.data?.deflectionRate ?? 0) * PERCENT);

  const addEntry = async (q: string, a: string, unansweredId?: string) => {
    if (!q.trim() || !a.trim()) {
      showToast(t(msg`Both the question and the answer are required`), "error");
      return;
    }
    try {
      const created = await upsert.mutateAsync({ question: q.trim(), answer: a.trim() });
      if (unansweredId) {
        await resolve.mutateAsync({ id: unansweredId, entryId: created.id });
      }
      setQuestion("");
      setAnswer("");
      showToast(t(msg`Added to the assistant's knowledge base`), "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : t(msg`Could not save`), "error");
    }
  };

  return (
    <div class="space-y-6" data-testid="academy-bot-panel-admin">
      <Show when={deflection.data} fallback={<Skeleton class="h-24 w-full rounded-xl" />}>
        {(stats) => (
          <div class="grid gap-3 sm:grid-cols-4">
            <div class="rounded-xl border border-border/40 p-4">
              <p class="text-2xl font-bold" data-testid="bot-deflection-rate">
                {rate()}%
              </p>
              <p class="text-xs text-muted-foreground">{t(msg`Resolved without a person`)}</p>
            </div>
            <div class="rounded-xl border border-border/40 p-4">
              <p class="text-2xl font-bold">{stats().conversations}</p>
              <p class="text-xs text-muted-foreground">{t(msg`Conversations this month`)}</p>
            </div>
            <div class="rounded-xl border border-border/40 p-4">
              <p class="text-2xl font-bold">{stats().escalated}</p>
              <p class="text-xs text-muted-foreground">{t(msg`Handed to a person`)}</p>
            </div>
            <div class="rounded-xl border border-border/40 p-4">
              <p class="text-2xl font-bold">{stats().unansweredOpen}</p>
              <p class="text-xs text-muted-foreground">{t(msg`Questions with no answer yet`)}</p>
            </div>
          </div>
        )}
      </Show>

      <section class="space-y-3">
        <h2 class="flex items-center gap-2 text-lg font-semibold">
          <MessageCircleQuestion class="h-4 w-4" />
          {t(msg`Questions the assistant could not answer`)}
        </h2>
        <p class="text-sm text-muted-foreground">
          {t(
            msg`Each one is a line missing from the knowledge base. Write the answer and the assistant stops handing that question to a person.`,
          )}
        </p>
        <ul class="space-y-2" data-testid="bot-unanswered-list">
          <For
            each={unanswered.data?.filter((u) => !u.resolved)}
            fallback={
              <li class="text-sm text-muted-foreground">
                {t(msg`Nothing pending. The assistant is answering everything it is asked.`)}
              </li>
            }
          >
            {(item) => {
              const { hits } = item;
              return (
                <li
                  class="rounded-lg border border-border/40 p-3"
                  data-testid="bot-unanswered-item"
                >
                  <p class="text-sm font-medium">{item.question}</p>
                  <p class="text-xs text-muted-foreground">{t(msg`Asked ${hits} times`)}</p>
                  <div class="mt-2 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setQuestion(item.question);
                      }}
                      data-testid="bot-unanswered-write"
                    >
                      <Plus class="mr-1 h-3.5 w-3.5" />
                      {t(msg`Write the answer`)}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void resolve.mutateAsync({ id: item.id })}
                      data-testid="bot-unanswered-dismiss"
                    >
                      <Check class="mr-1 h-3.5 w-3.5" />
                      {t(msg`Dismiss`)}
                    </Button>
                  </div>
                </li>
              );
            }}
          </For>
        </ul>
      </section>

      <section class="space-y-3">
        <h2 class="text-lg font-semibold">{t(msg`Knowledge base`)}</h2>
        <div class="space-y-2 rounded-xl border border-border/40 p-4">
          <input
            class="w-full rounded-lg border-2 border-border bg-field-background px-3 py-2"
            placeholder={t(msg`Question, as people ask it`)}
            value={question()}
            onInput={(e) => {
              setQuestion(e.currentTarget.value);
            }}
            data-testid="bot-entry-question"
          />
          <textarea
            class="w-full rounded-lg border-2 border-border bg-field-background px-3 py-2"
            rows={3}
            placeholder={t(
              msg`Answer. Be exact with prices and dates: the assistant repeats it word for word.`,
            )}
            value={answer()}
            onInput={(e) => {
              setAnswer(e.currentTarget.value);
            }}
            data-testid="bot-entry-answer"
          />
          <Button
            size="sm"
            disabled={!question().trim() || !answer().trim() || upsert.isPending}
            onClick={() => void addEntry(question(), answer())}
            data-testid="bot-entry-save"
          >
            {t(msg`Add`)}
          </Button>
        </div>
        <ul class="space-y-2" data-testid="bot-entries-list">
          <For each={entries.data}>
            {(entry) => (
              <li
                class="flex items-start gap-3 rounded-lg border border-border/40 p-3"
                data-testid="bot-entry-item"
              >
                <div class="min-w-0 flex-1">
                  <p class="text-sm font-medium">{entry.question}</p>
                  <p class="text-xs text-muted-foreground">{entry.answer}</p>
                </div>
                <button
                  type="button"
                  class="text-muted-foreground hover:text-danger"
                  onClick={() => void remove.mutateAsync(entry.id)}
                  aria-label={t(msg`Delete`)}
                  data-testid="bot-entry-delete"
                >
                  <Trash2 class="h-4 w-4" />
                </button>
              </li>
            )}
          </For>
        </ul>
      </section>
    </div>
  );
};
